import _get from 'lodash/get'
import * as z from 'zod'

import type { IntlFormatter } from '@nxt-insurance/formatting'

import { CONTENTS_NXT_CONSTANTS } from '@flows/configs/contentsNxt/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import { t } from '@flows/core/i18n'
import type { ConditionValues } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration, FlowValues } from '@flows/core/types/stepTypes'
import { BaseSchema_ContentsV1GetsafeDe } from '@flows/insurance/schemas'

const getErrorMap =
  (formatter: IntlFormatter): z.ZodErrorMap =>
  (issue, ctx) => {
    switch (issue.code) {
      case z.ZodIssueCode.too_small:
        return {
          message: t('sharedFlows_contentsNxt:steps.contents_insured_amount.input.error.min', {
            min: formatter.formatCurrency(issue.minimum),
          }),
        }
      case z.ZodIssueCode.too_big:
        return {
          message: t('sharedFlows_contentsNxt:steps.contents_insured_amount.input.error.max', {
            max: formatter.formatCurrency(issue.maximum),
          }),
        }
      default:
        return { message: ctx.defaultError }
    }
  }

const contents_insured_amount = (params: StepConfigParams) => {
  const constants = CONTENTS_NXT_CONSTANTS
  const getRecommendedInsuredSum = (values?: ConditionValues | null): number =>
    Number.parseInt(
      z.coerce
        .string()
        .parse(_get(values, params.helperFunctions.getParamPath('living_space_square_meters')))
    ) * constants.defaultSquareMeterPrice

  return {
    schema: schemaUtils.generateZodObjectFromPaths({
      [params.helperFunctions.getParamPath('insured_sum_amount')]:
        BaseSchema_ContentsV1GetsafeDe.shape.insured_sum_amount,
    }),
    // We add a custom error map based on the validation messages that we need for this step
    // this allows us to overwrite only the errors we need without needing to change the schema
    errorMap: getErrorMap(params.clientHelpers.formatter),
    stepContent: [
      {
        type: 'title',
        text: {
          type: 'translatableText',
          translationKey: 'sharedFlows_contentsNxt:steps.contents_insured_amount.title',
        },
      },
      {
        type: 'input',
        shouldTrackValues: true,
        name: params.helperFunctions.getParamPath('insured_sum_amount'),
        label: { type: 'translatableText', translationKey: 'sharedFlows_common:emptyLine' },
        inputType: 'integer',
      },
      {
        type: 'customComponent',
        name: 'UnderInsuredWarning',
        props: {
          insuredSumPath: params.helperFunctions.getParamPath('insured_sum_amount'),
          getRecommendedInsuredSum,
          warningModalConfiguration: [
            {
              type: 'title',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents_insured_amount.modalWarning.title',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents_insured_amount.modalWarning.text1',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents_insured_amount.modalWarning.text2',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents_insured_amount.modalWarning.text3',
              },
            },
            {
              type: 'text',
              text: {
                type: 'translatableText',
                translationKey:
                  'sharedFlows_contentsNxt:steps.contents_insured_amount.modalWarning.text4',
              },
            },
          ],
        },
        inputs: [],
        outputs: [],
        condition: {
          type: 'function',
          function: (values) => {
            return (
              z.coerce
                .number()
                .parse(_get(values, params.helperFunctions.getParamPath('insured_sum_amount'))) <
                getRecommendedInsuredSum(values) &&
              Number.parseInt(
                z.coerce
                  .string()
                  .parse(_get(values, params.helperFunctions.getParamPath('insured_sum_amount')))
              ) >= constants.insuredSumValidation.min
            )
          },
          valuePaths: [
            params.helperFunctions.getParamPath('insured_sum_amount'),
            params.helperFunctions.getParamPath('living_space_square_meters'),
          ],
        },
      },
      {
        type: 'infoBox',
        showInfoIcon: true,
        contentConfiguration: [
          {
            type: 'title',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_contentsNxt:steps.contents_insured_amount.infobox.terms.title',
            },
          },
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_contentsNxt:steps.contents_insured_amount.infobox.terms.text1',
            },
          },
          {
            type: 'text',
            text: {
              type: 'translatableText',
              translationKey:
                'sharedFlows_contentsNxt:steps.contents_insured_amount.infobox.terms.text2',
            },
          },
        ],
      },
    ],
    nextStep: 'contents_permanent_residence',
    subFlow: params.subFlow,
    onPrepare: {
      type: 'simpleHook',
      callback: (flowValues: FlowValues) => {
        return {
          formValues: {
            [params.helperFunctions.getParamPath('insured_sum_amount')]:
              getRecommendedInsuredSum(flowValues),
          },
        }
      },
    },
  } as const satisfies AnyStepConfiguration
}

export default contents_insured_amount
